import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonIcon, IonItem, IonRadio, IonRadioGroup } from "@ionic/react"
import { CalculatorParameterForm } from "./FormTypes"
import { JSX, useEffect, useMemo, useState } from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { arrowDownOutline, createOutline } from "ionicons/icons"
import { InputAdornment, TextField, Typography } from "@mui/material"
import { db } from "../../../data/db/db"
import { ArticleSpecs } from "../../../models/articleModel"
import { getMaxAEValue, getMaxAPValue, mapSpindle } from "./formHelpers"
import { useTranslation } from "react-i18next"
import { Material } from "../../../models/materialModel"

interface ParameterFormProps {
    isFormShowing: boolean,
    submitForm: (data: any) => void,
    strategy: string | undefined,
    diameter: string | undefined,
    ident: string | undefined | null,
    pos: string | undefined,
    editBtnCallback: () => void,
    formData: CalculatorParameterForm | undefined,
    material: Material | null | undefined
}

const getInitialAe = (strategy: string, diameter: string) => {
    if (diameter !== "") {
        if (strategy === "std-vsp") {
            return Number(diameter);
        } else if (strategy === "std-ko") {
            const val = Number(diameter) * 0.03
            return (val);
        }
        return undefined
    }
    return undefined
}

/** Für Vollspur/Kopieren: ae aus DB-d1, sobald Spec da ist (zuverlässiger als Parent-Durchmesser z.B. nach Toolfinder-Link). */
const resolveInitialAe = (
    strategy: string | undefined,
    diameter: string | undefined,
    articleSpec: ArticleSpecs | undefined
): number | undefined => {
    if (!strategy) return undefined
    if (articleSpec && (strategy === "std-vsp" || strategy === "std-ko")) {
        return getInitialAe(strategy, String(articleSpec.d1))
    }
    if (diameter !== undefined && diameter !== "") {
        return getInitialAe(strategy, diameter)
    }
    return undefined
}

const ParameterForm: React.FC<ParameterFormProps> = ({ isFormShowing, submitForm, strategy, diameter, ident, pos, editBtnCallback, formData, material }: ParameterFormProps) => {
    const [data, setData] = useState<CalculatorParameterForm | undefined>()
    const [articleSpec, setArticleSpec] = useState<undefined | ArticleSpecs>()
    const { control, reset, handleSubmit, formState: { errors }, watch } = useForm<CalculatorParameterForm>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: useMemo(() => {
            return {
                ae: resolveInitialAe(strategy, diameter, articleSpec),
                ap: formData && formData.ap ? Number(formData.ap) : undefined,
                maschineSpeed: undefined,
                spindle: "motor"
            }
        }, [strategy, diameter, formData, articleSpec])
    });

    const [maxAe, setMaxAe] = useState<{ value: number; message: JSX.Element; }>();
    const { t } = useTranslation();

    const selectedAE = watch("ae")
    const aeLockedByStrategy = strategy === "std-vsp" || strategy === "std-ko"

    const onSubmit: SubmitHandler<CalculatorParameterForm> = data => {
        setData(data)
        //console.log(data)
        submitForm(data)
    }

    useEffect(() => {
        reset({
            ae: resolveInitialAe(strategy, diameter, articleSpec),
            ap: formData && formData.ap ? Number(formData.ap) : undefined,
            maschineSpeed: undefined,
            spindle: "motor"
        })
    }, [strategy, diameter, reset, formData, articleSpec])

    useEffect(() => {
        if (formData === undefined) {
            setData(undefined)
        }
    }, [formData])


    useEffect(() => {
        let active = true;

        if (ident && pos) {
            (async () => {
                const articleFromDB = await db.articlesSpec.where({ ident: ident, pos: pos }).first();

                if (active && articleFromDB) {
                    setArticleSpec(articleFromDB)
                }
            })();
        }

        return () => {
            active = false;
        };
    }, [ident, pos])

    useEffect(() => {
        const fetchMaxAE = async () => {
            if (articleSpec && strategy && material) {
                let res: { value: number; message: JSX.Element; } = await getMaxAEValue(articleSpec, strategy, material.gruppe);
                setMaxAe(res);
            }
        };

        fetchMaxAE();
    }, [material, articleSpec, strategy]);



    if (isFormShowing) {

        return (
            <>
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>{t("calculatorForm.Parameter")}</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div>
                            {articleSpec && <>
                                <b>d<sub>1</sub>:</b> {Number(articleSpec.d1).toLocaleString()} mm <b>d<sub>2</sub>:</b> {Number(articleSpec.d2).toLocaleString()} mm <b>d<sub>3</sub>:</b> {Number(articleSpec.d3).toLocaleString()} mm <br />
                                <b>l<sub>1</sub>:</b> {Number(articleSpec.l1).toLocaleString()} mm <b>l<sub>2</sub>:</b> {Number(articleSpec.l2).toLocaleString()} mm <b>l<sub>3</sub>:</b> {Number(articleSpec.l3).toLocaleString()} mm<br />
                            </>}
                            {strategy === "std-vsp" && <b>{t("calculatorForm.StdParameterForm.Achtung")}: a<sub>p</sub> {t("calculatorForm.StdParameterForm.maximal")} 1xD</b>}
                        </div>
                        <form>
                            {/* a_e */}
                            <div style={{ display: "flex", gap: 30, marginTop: 10 }}>
                                <div>
                                    <Controller
                                        control={control}
                                        name="ae"
                                        rules={{
                                            required: { value: true, message: t("Feld darf nicht leer sein") },
                                            min: { value: 0.001, message: t("calculatorForm.StdParameterForm.Wert muss größer als 0.001 sein") },
                                            //max: { value: articleSpec && strategy && material ? getMaxAEValue(articleSpec, strategy, material.gruppe).value : 20, message: t("calculatorForm.StdParameterForm.Wert zu groß") }
                                            max: { value: maxAe?.value ? maxAe.value : 20, message: t("calculatorForm.StdParameterForm.Wert zu groß") }
                                        }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <TextField
                                                variant="standard"
                                                label={<Typography>a<sub>e</sub></Typography>}
                                                value={value ?? ""}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                type="number"
                                                /* readOnly statt disabled: RHF v7 nimmt disabled-Felder nicht in Submit-Daten auf */
                                                InputProps={{
                                                    readOnly: aeLockedByStrategy,
                                                    endAdornment: <InputAdornment position="start">mm</InputAdornment>,
                                                }}
                                                sx={aeLockedByStrategy ? {
                                                    "& .MuiInputBase-input": { cursor: "default", color: "text.secondary" },
                                                } : undefined}
                                            />
                                        )}
                                    />

                                    {/* @ts-ignore */}
                                    {errors.ae && <div className="error">{errors.ae.message}</div>}
                                </div>

                                {/* a_p */}
                                <div>
                                    <Controller
                                        control={control}
                                        name="ap"
                                        rules={{
                                            required: { value: true, message: t("Feld darf nicht leer sein") },
                                            min: { value: 0.001, message: t("calculatorForm.StdParameterForm.Wert muss größer als 0.001 sein") },
                                            max: { value: articleSpec && strategy && selectedAE ? getMaxAPValue(articleSpec, strategy, selectedAE).value : 20, message: t("calculatorForm.StdParameterForm.Wert zu groß") }
                                        }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <TextField
                                                variant="standard"
                                                label={<Typography>a<sub>p</sub></Typography>}
                                                value={value ?? ""}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                type="number"
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="start">mm</InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />

                                    {/* @ts-ignore */}
                                    {errors.ap && <div className="error">{errors.ap.message}</div>}
                                </div>
                            </div>

                            {articleSpec && strategy && errors.ae?.message === t("calculatorForm.StdParameterForm.Wert zu groß") && <div className="calcErrorBox">
                                {maxAe?.message}
                            </div>}
                            {(articleSpec && strategy && selectedAE && selectedAE > 0 && errors.ap?.message === t("calculatorForm.StdParameterForm.Wert zu groß")) && <div className="calcErrorBox">
                                {getMaxAPValue(articleSpec, strategy, selectedAE).message}
                            </div>}

                            {/* MaschineSpeed */}
                            <div style={{ marginTop: 10 }}>
                                <Controller
                                    control={control}
                                    name="maschineSpeed"
                                    rules={{
                                        min: { value: 1, message: t("calculatorForm.StdParameterForm.Drehzahl muss größer als 0 sein") },
                                        max: { value: 50000, message: t("calculatorForm.StdParameterForm.Drehzahl darf maximal 50000 sein") },
                                    }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <TextField
                                            variant="standard"
                                            label={<Typography>{t("calculatorForm.StdParameterForm.Maschinendrehzahl")} ({t("calculatorForm.StdParameterForm.optional")})</Typography>}
                                            value={value === undefined ? "" : value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            style={{ width: "100%" }}
                                            type="number"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="start">U/min</InputAdornment>,
                                            }}
                                        />
                                    )}
                                />
                                {/* @ts-ignore */}
                                {errors.maschineSpeed && <div className="error">{errors.maschineSpeed.message}</div>}

                            </div>

                            {/* Spindle */}
                            <div style={{ marginTop: 10 }}>
                                <Typography>{t("calculatorForm.StdParameterForm.Spindelart")}</Typography>
                                <Controller
                                    control={control}
                                    name="spindle"
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                    }}
                                    render={({ field: { value, onChange } }) => (
                                        <IonRadioGroup value={value} onIonChange={({ detail: { value } }) => onChange(value)}>
                                            <IonItem>
                                                <IonRadio value="motor" labelPlacement="end" justify="start">
                                                    {t("calculatorForm.StdParameterForm.Motorspindel")}
                                                </IonRadio>
                                            </IonItem>
                                            <IonItem>
                                                <IonRadio value="getriebe" labelPlacement="end" justify="start">
                                                    {t("calculatorForm.StdParameterForm.Getriebespindel")}
                                                </IonRadio>
                                            </IonItem>
                                        </IonRadioGroup>
                                    )}
                                />
                                {/* @ts-ignore */}
                                {errors.spindle && <div className="error">{errors.spindle.message}</div>}
                            </div>



                            {/* Submit */}
                            <IonButton style={{ marginTop: 24 }} onClick={handleSubmit(onSubmit)} expand="block">
                                <IonIcon slot="start" icon={arrowDownOutline} />{t("calculatorForm.StdParameterForm.Berechnen")}
                            </IonButton>
                        </form>
                    </IonCardContent>
                </IonCard>
            </>
        )
    } else {
        if (data) {
            return (
                <IonCard color="light" >
                    <IonCardContent>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div>
                                {articleSpec && <div>
                                    <b>d<sub>1</sub>:</b> {Number(articleSpec.d1).toLocaleString()} mm <b>d<sub>2</sub>:</b> {Number(articleSpec.d2).toLocaleString()} mm <b>d<sub>3</sub>:</b> {Number(articleSpec.d3).toLocaleString()} mm <br />
                                    <b>l<sub>1</sub>:</b> {Number(articleSpec.l1).toLocaleString()} mm <b>l<sub>2</sub>:</b> {Number(articleSpec.l2).toLocaleString()} mm <b>l<sub>3</sub>:</b> {Number(articleSpec.l3).toLocaleString()} mm<br />
                                    <b>a<sub>e</sub></b>: {Number(data.ae).toLocaleString()} mm <b>a<sub>p</sub></b>: {Number(data.ap).toLocaleString()} mm <br />
                                    {data.maschineSpeed !== undefined && <span><b>{t("calculatorForm.StdParameterForm.Maschinendrehzahl")}</b>: {data.maschineSpeed} U/min<br /></span>}
                                    <b>{t("calculatorForm.StdParameterForm.Spindelart")}</b>: {mapSpindle(data.spindle!)}
                                </div>}
                            </div>
                            <div>
                                <IonButton fill="outline" onClick={editBtnCallback}>
                                    <IonIcon icon={createOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    </IonCardContent>
                </IonCard>
            )
        }
        return null
    }

}

export default ParameterForm
